import { AdminContainer } from "../../components/layouts";

import {
  Button,
  ListItem,
  Paper,
  Typography,
  TextField,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Check as CheckIcon, Close } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import api from "../../api";
import { useLoading } from "../../hooks/loading";
import { Check } from "./Check";
import { DetailsView } from "./DetailsView";
import { toast } from "react-toastify";
import CopyContentIconButton from "../../components/CopyContentIconButton";
import { useDocumentsState } from "../../components/DocumentsList";

export default function EnrollmentVerificationDetailPage() {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();

  const { startLoading, stopLoading } = useLoading();

  const [verification, setVerification] = useState(null);
  const [memo, setMemo] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = React.useState(0);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        const result = await api.getEnrollmentVerification(id);
        setVerification(result.data.enrollmentVerification);
      } catch (err) {
        toast.error(err.message);
      }
      stopLoading();
    })();
  }, [id]);

  useEffect(() => {
    if (verification && !memo) setMemo(verification.memo);
  }, [verification]);

  const updateVerificationStatus = async (status) => {
    startLoading();
    try {
      const apiMethod =
        status === "approve"
          ? api.postApproveEnrollmentVerification
          : api.postRejectEnrollmentVerification;

      const result = await apiMethod(id, memo);
      setVerification(result.data.enrollmentVerification);
    } catch (err) {
      toast.error(err.message);
    }
    stopLoading();
  };

  const restartVerification = async () => {
    startLoading();
    try {
      const result = await api.restartEnrollmentVerification(verification.id);
      history.push(`/enrollment-verifications/${result.data.enrollmentVerification.id}`);
    } catch (err) {
      toast.error(err.message);
    }
    stopLoading();
  };

  const handleFilesDropped = async (files) => {
    startLoading();
    try {
      files.map((file) => {
        api
          .createPresignedDocumentUrl({
            customerId: verification.customer.id,
            filename: file.name,
          })
          .then(api.pickData)
          .then((d) => {
            return fetch(d.presignedUrl, {
              method: "PUT",
              body: file,
              mode: "cors",
            }).then(() => d);
          })
          .then((d) => {
            api.addDocumentForReportingAccountEnrollment({
              id: verification.enrollment.enrollmentCaseId,
              s3_url: d.presignedUrl,
              entity: "reporting_account_enrollment",
            });
          });
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      const result = await api.getEnrollmentVerification(id);
      setVerification(result.data.enrollmentVerification);
      stopLoading();
    }
  };

  const areAllChecksCompleted = !!verification?.checks.find((check) => !check.status);

  if (!verification) return "";

  return (
    <AdminContainer variant="detail-wide" className={classes.container}>
      <Paper elevation={3} className={classes.paperContainer}>
        <div className={classes.checksContainer}>
          <div className={classes.verificationTitleContainer}>
            <h2>{verification.kind} verification</h2>
            {verification.status !== "pending" && (
              <div>
                <div className={`${classes.verificationStatus}`}>
                  {verification.status === "approved" ? (
                    <CheckIcon className={classes.verificationStatusApproved} />
                  ) : (
                    <Close className={classes.verificationStatusRejected} />
                  )}
                  <Typography
                    variant="h6"
                    style={{
                      color: verification.status === "approved" ? "green" : "red",
                    }}
                  >
                    {verification.status}
                  </Typography>
                </div>
                {verification.canRestart ? (
                  <Button
                    onClick={restartVerification}
                    variant="contained"
                    color="primary"
                  >
                    Restart Verification
                  </Button>
                ) : null}
              </div>
            )}
          </div>

          <ListItemText>
            Enrollment address: {verification.enrollment.oneLineAddress}
            <CopyContentIconButton value={verification.enrollment.oneLineAddress} />
          </ListItemText>

          {verification?.checks?.map((check) => (
            <Check
              key={check.id}
              check={check}
              enrollmentVerificationId={id}
              setEnrollmentVerification={setVerification}
              enrollmentVerification={verification}
            />
          ))}

          <ListItem className={classes.actionContainer}>
            <TextField
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              disabled={verification.status !== "pending"}
              fullWidth
              label="Memo"
              multiline
              rows={4}
              variant="outlined"
            />
            {verification.status === "pending" && (
              <div className={classes.actionButtonContainer}>
                <Button
                  onClick={() => updateVerificationStatus("approve")}
                  variant="contained"
                  color="primary"
                  disabled={areAllChecksCompleted}
                >
                  Approve
                </Button>
                <Button
                  className={classes.rejectButton}
                  variant="outlined"
                  color="primary"
                  disabled={areAllChecksCompleted || !(memo && memo.length > 16)}
                  onClick={() => updateVerificationStatus("reject")}
                >
                  Reject
                </Button>
              </div>
            )}
          </ListItem>
        </div>

        <div className={classes.detailsViewContainer}>
          <DetailsView
            onFilesDropped={handleFilesDropped}
            verification={verification}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            files={files}
            setFiles={setFiles}
            selectedFileIndex={selectedFileIndex}
            setSelectedFileIndex={setSelectedFileIndex}
          />
        </div>
      </Paper>
    </AdminContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    marginBottom: 0,
  },
  verificationTitleContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  paperContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    alignItems: "stretch",
    height: "89vh",
  },
  checksContainer: {
    flex: 2,
    height: "100%",
    padding: 16,
    overflowY: "scroll",
  },
  detailsViewContainer: {
    flex: 3,
    height: "100%",
    overflowY: "scroll",
  },
  checkInput: {
    marginBottom: 16,
  },
  checkActionButtonContainer: {
    display: "flex",
    flexDirection: "row",
    marginLeft: 16,
  },
  actionContainer: {
    marginTop: 32,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  actionButtonContainer: {
    display: "flex",
    flexDirection: "row",
    marginTop: 16,
  },
  rejectButton: {
    marginLeft: 16,
  },
  verificationStatus: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  verificationStatusApproved: {
    color: theme.palette.success.light,
  },
  verificationStatusRejected: {
    color: theme.palette.error.light,
  },
}));
